import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    Card,
    CardContent,
    Switch,
    TextField,
    Dialog,
    Slide
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { PAContext } from "../../../Services/PAContext";
import { cancelChipStyle } from "../../ButtonStyle/Cancel";
import { addChipStyle } from "../../ButtonStyle/Add";

// Native-style slide transition from bottom
const Transition = React.forwardRef(function Transition(props, ref) {
    const { children, ...other } = props;
    return (
        <Slide direction="up" ref={ref} {...other}>
            {children}
        </Slide>
    );
});

/**
 * @param {Object} editingDoc - The document object to edit
 * @param {Boolean} open - Dialog open state
 * @param {Function} onClose - Function to close the dialog
 */
function DocumentEditCard({ editingDoc, open, onClose }) {
    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, setAccountDocumentArray, sendSocketMessage } = useContext(PAContext);


    // --- Local Form State ---
    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [isPrivate, setIsPrivate] = useState(true);
    const [hasExpirationDate, setHasExpirationDate] = useState(false);
    const [expirationDate, setExpirationDate] = useState("");

    // Sync state when editingDoc changes
    useEffect(() => {
        if (editingDoc) {
            setTitle(editingDoc.name ?? "");
            setNotes(editingDoc.notes ?? "");
            setIsPrivate(editingDoc.private != null ? Boolean(editingDoc.private) : true);
            setHasExpirationDate(Boolean(editingDoc.expires));
            setExpirationDate(editingDoc.expires || "");
        }
    }, [editingDoc]);

    // --- Handlers ---
    const handleTitleChange = (e) => setTitle(e.target.value);
    const handleNotesChange = (e) => setNotes(e.target.value);
    const handleTogglePrivate = () => setIsPrivate(!isPrivate);

    const handleToggleExpiration = (e) => {
        const checked = e.target.checked;
        setHasExpirationDate(checked);
        if (!checked) setExpirationDate("");
    };

    const handleDateSelected = (date) => {
        setExpirationDate(date ? date.format("YYYY-MM-DD") : "");
    };

    const handleSave = () => {
        if (!title.trim()) return alert("Document title is required.");

        const updateData = {
            id: editingDoc.id,
            name: title,
            notes: notes,
            expires: expirationDate,
            private: isPrivate
        };

        if (editingDoc.status === 1) {
            // 1. Update Account Document
            setAccountDocumentArray((prev) =>
                prev.map(d => String(d.id) === String(editingDoc.id) ? { ...d, ...updateData } : d)
            );

            if (PA_GlobalObject.Planning.status!== 10) {
                sendSocketMessage({
                    action: "E_updateAccountDocumentFields",
                    document_id: editingDoc.id,
                    ...updateData
                });
            }
        } else {
            // 2. Update Event Document
            set_PA_GlobalObject((prev) => {
                const planning = prev?.Planning ?? {};
                const docs = Array.isArray(planning.documents) ? planning.documents : [];
                const updatedDocs = docs.map(d =>
                    String(d.id) === String(editingDoc.id) ? { ...d, ...updateData } : d
                );
                return { ...prev, Planning: { ...planning, documents: updatedDocs } };
            });

            if (PA_GlobalObject.Planning.status!== 10) {
                sendSocketMessage({
                    action: "E_updateEventDocument",
                    event_id: PA_GlobalObject.Planning.id,
                    document_id: editingDoc.id,
                    ...updateData
                });
            }
        }

        onClose();
    };

    if (!editingDoc) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            PaperProps={{ sx: {
                backgroundColor: '#f8f9fa',
                    minWidth: 350, // or '350px'
                    maxWidth: 500, // optional
                    width: '100%', // allows it to scale on smaller screens
            } }}
        >
            {/* STICKY HEADER */}
            <Box sx={{
                flexShrink: 0,
                zIndex: 100,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                p: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eef0f2'
            }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#151269', lineHeight: 1 }}>
                        Edit Item
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, fontSize: '9px' }}>
                        ID: {String(editingDoc.id).substring(0, 8).toUpperCase()}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip sx={cancelChipStyle} label="Cancel" onClick={onClose} />
                    <Chip sx={addChipStyle} label="Save" onClick={handleSave} />
                </Box>
            </Box>

            {/* SCROLLABLE CONTENT */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', p: 2, pb: 10 }}>

                {/* IDENTITY */}
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#151269', opacity: 0.6, mb: 0.5, display: 'block' }}>Identity</Typography>
                <Card sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #e0e4e8', mb: 2 }}>
                    <CardContent sx={{ p: '12px !important' }}>
                        <TextField
                            variant="standard"
                            placeholder="Document Title"
                            fullWidth
                            value={title}
                            onChange={handleTitleChange}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '16px', fontWeight: 700, color: '#151269' } }}
                        />
                        <Box sx={{ height: '1px', bgcolor: '#eee', my: 1 }} />
                        <TextField
                            placeholder="Notes (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={notes}
                            onChange={handleNotesChange}
                            variant="standard"
                            InputProps={{ disableUnderline: true, sx: { fontSize: '13px', color: '#666' } }}
                        />
                    </CardContent>
                </Card>

                {/* PROTECTION */}
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#151269', opacity: 0.6, mb: 0.5, display: 'block' }}>Privacy</Typography>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e0e4e8', boxShadow: 'none', mb: 2, p: '12px' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#151269' }}>
                            {isPrivate ? 'Private Item' : 'Public Item'}
                        </Typography>
                        <Switch size="small" checked={isPrivate} onChange={handleTogglePrivate} />
                    </Box>
                </Card>

                {/* EXPIRY */}
                <Typography variant="overline" sx={{ fontWeight: 800, color: '#151269', opacity: 0.6, mb: 0.5, display: 'block' }}>Expiration</Typography>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e0e4e8', boxShadow: 'none', mb: 2, p: '12px' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#151269' }}>Track Expiry Date</Typography>
                        <Switch size="small" checked={hasExpirationDate} onChange={handleToggleExpiration} />
                    </Box>
                    {hasExpirationDate && (
                        <Box sx={{ mt: 1, borderTop: '1px solid #eee', pt: 1 }}>
                            <DateCalendar
                                sx={{ width: '100%' }}
                                value={expirationDate ? dayjs(expirationDate) : null}
                                onChange={handleDateSelected}
                            />
                        </Box>
                    )}
                </Card>
            </Box>
        </Dialog>
    );
}

export default DocumentEditCard;